/** Live / final voice-reference readiness checklist (shared rules). */

import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { VoiceReferenceQualityAnalysis } from '../../../../voiceReference/voiceReferenceQualityPolicy'
import {
  evaluateRecordingReadiness,
  PRODUCT_RECOMMENDED_MAX_SECONDS,
  PRODUCT_RECOMMENDED_MIN_SECONDS,
  type LiveAudioStats,
  type ReadinessEvalMode,
  type RecordingReadinessItem,
} from '../../../../voiceReference/voiceReferenceReadiness'

export type ReadinessChecklistLayout = 'stack' | 'responsive-inline'

type Props = {
  mode: ReadinessEvalMode
  durationMs: number
  live?: LiveAudioStats | null
  analysis?: VoiceReferenceQualityAnalysis | null
  layout?: ReadinessChecklistLayout
  className?: string
}

function statusIcon(status: RecordingReadinessItem['status']) {
  if (status === 'pass') return <CheckCircleFilled aria-hidden />
  if (status === 'fail') return <CloseCircleFilled aria-hidden />
  if (status === 'pending') return <span className="pcv-voice-readiness-dot is-filled" aria-hidden />
  return <span className="pcv-voice-readiness-dot" aria-hidden />
}

function itemLabel(
  item: RecordingReadinessItem,
  t: ReturnType<typeof useTranslation<'canvas'>>['t'],
): string {
  switch (item.id) {
    case 'min_duration':
      return t('voiceTemplate.referenceCapture.readiness.minDurationShort', {
        min: item.targetSeconds ?? 20,
        defaultValue: t('voiceTemplate.referenceCapture.readiness.minDuration', {
          min: item.targetSeconds ?? 20,
        }),
      })
    case 'speech_presence':
      return t('voiceTemplate.referenceCapture.readiness.speechPresence')
    case 'silence_ratio':
      return t('voiceTemplate.referenceCapture.readiness.silenceRatioShort', {
        defaultValue: t('voiceTemplate.referenceCapture.readiness.silenceRatio'),
      })
    case 'recommended_duration':
      return t('voiceTemplate.referenceCapture.readiness.recommendedDurationShort', {
        recMin: PRODUCT_RECOMMENDED_MIN_SECONDS,
        recMax: PRODUCT_RECOMMENDED_MAX_SECONDS,
        defaultValue: t('voiceTemplate.referenceCapture.readiness.recommendedDuration', {
          recMin: PRODUCT_RECOMMENDED_MIN_SECONDS,
          recMax: PRODUCT_RECOMMENDED_MAX_SECONDS,
        }),
      })
    default:
      return item.id
  }
}

function itemProgress(item: RecordingReadinessItem): string | null {
  if (item.id === 'min_duration' || item.id === 'recommended_duration') {
    const cur = Math.floor(item.currentSeconds ?? 0)
    const target = item.targetSeconds ?? 0
    return `${cur}/${target}s`
  }
  if (item.id === 'speech_presence' && item.currentSeconds != null) {
    return `${item.currentSeconds.toFixed(1)}s`
  }
  if (item.id === 'silence_ratio' && item.detail) return item.detail
  return null
}

function statusText(
  status: RecordingReadinessItem['status'],
  t: ReturnType<typeof useTranslation<'canvas'>>['t'],
): string {
  if (status === 'pass') return t('voiceTemplate.referenceCapture.readiness.statusPass')
  if (status === 'fail') return t('voiceTemplate.referenceCapture.readiness.statusFail')
  if (status === 'pending') return t('voiceTemplate.referenceCapture.readiness.statusPending')
  return t('voiceTemplate.referenceCapture.readiness.statusIdle')
}

function VoiceRecordingReadinessChecklistImpl({
  mode,
  durationMs,
  live,
  analysis,
  layout = 'responsive-inline',
  className,
}: Props) {
  const { t } = useTranslation('canvas')
  const snapshot = useMemo(
    () => evaluateRecordingReadiness({ mode, durationMs, live, analysis }),
    [analysis, durationMs, live, mode],
  )

  return (
    <div
      className={`pcv-voice-readiness-checklist is-${layout}${className ? ` ${className}` : ''}`}
      data-testid="canvas-voice-readiness-checklist"
      data-layout={layout}
      data-hard-pass={snapshot.hardPass ? 'yes' : 'no'}
    >
      <div className="pcv-voice-readiness-title">{t('voiceTemplate.referenceCapture.readiness.title')}</div>
      <ul className="pcv-voice-readiness-list">
        {snapshot.items.map((item) => {
          const progress = itemProgress(item)
          return (
            <li
              key={item.id}
              className={`pcv-voice-readiness-item is-${item.status}${item.kind === 'recommended' ? ' is-recommended' : ''}`}
              data-testid={`canvas-voice-readiness-${item.id}`}
              data-status={item.status}
            >
              <span className="pcv-voice-readiness-icon">{statusIcon(item.status)}</span>
              <span className="pcv-voice-readiness-label">
                {itemLabel(item, t)}
                <span className="pcv-voice-readiness-sr">{statusText(item.status, t)}</span>
              </span>
              {progress ? <span className="pcv-voice-readiness-progress">{progress}</span> : null}
            </li>
          )
        })}
      </ul>
      {mode === 'live' && snapshot.hardPass ? (
        <div className="pcv-voice-readiness-inline-ok" data-testid="canvas-voice-readiness-met">
          {t('voiceTemplate.referenceCapture.readiness.canStop')}
        </div>
      ) : null}
      {mode === 'final' && snapshot.hardPass ? (
        <div className="pcv-voice-readiness-inline-ok" data-testid="canvas-voice-readiness-met">
          {t('voiceTemplate.referenceCapture.readiness.allMetShort')}
        </div>
      ) : null}
      {mode !== 'idle' && !snapshot.hardPass ? (
        <div className="pcv-voice-readiness-inline-pending" data-testid="canvas-voice-readiness-unmet">
          {t('voiceTemplate.referenceCapture.readiness.notMet')}
        </div>
      ) : null}
    </div>
  )
}

const VoiceRecordingReadinessChecklist = memo(VoiceRecordingReadinessChecklistImpl)
export default VoiceRecordingReadinessChecklist
